import type {
  Communication,
  QueueRecommendation,
} from '@entities/communication/model/types';
import type {Employee} from '@entities/employee/model/types';
import type {Period} from '@lib/atoms';

export interface KpiSnapshot {
  period: Period;
  callsTotal: number;
  callsPlan: number;
  callsDelta: number;
  avgScore: number;
  scoreDelta: number;
  conversion: number;
  convDelta: number;
  withResult: number;
  withResultDelta: number;
  withAgreement: number;
  withAgreementDelta: number;
  /** История оценок для sparkline */
  scoreHistory: number[];
}

export interface CommunicationsFilter {
  period?: Period;
  managerId?: string;
  isTargeted?: boolean;
  hasQueueRecommendation?: boolean;
  status?: 'sale' | 'noresult' | 'robot' | 'wrongNumber';
  search?: string;
}

export interface CommunicationsResponse {
  items: Communication[];
  total: number;
}

// ─── Контракт ленты коммуникаций (docs/BACKEND_CONTRACTS.md) ────────────────

/** Стадия конвейера обработки коммуникации. */
export type ProcessingStatus =
  | 'pending'
  | 'ingested'
  | 'preprocessing'
  | 'ready'
  | 'partial'
  | 'failed';

/** Статус анализа (result-pipeline) коммуникации. */
export type AnalysisResultStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed';

/** Человекочитаемые подписи статусов обработки. */
export const PROCESSING_STATUS_LABELS: Record<ProcessingStatus, string> = {
  pending: 'В очереди',
  ingested: 'Принят',
  preprocessing: 'Транскрибация',
  ready: 'Обработан',
  partial: 'Частично',
  failed: 'Ошибка',
};

/** Сортировка ленты: поле из allowlist бэка + направление. */
export interface FeedSort {
  field: string;
  direction: 'asc' | 'desc';
}

/** Тело запроса `POST /workspaces/{ws}/communications/feed`. */
export interface FeedRequest {
  /** Без planSid и communicationKind — только базовый план воркспейса. */
  planSid?: string | null;
  /** Раздел ленты: что считать встречей, решает бэк (канал `meeting` + правила
   *  активных планов встреч); без planSid — слияние результатов планов раздела.
   *  `all` — оба раздела, у каждой строки планы её раздела (звонку — планы
   *  звонков, встрече — встреч): оценка и результат как в ленте раздела. */
  communicationKind?: 'call' | 'meeting' | 'all';
  /** MQL-фильтр по allowlist-путям (`occurredAt`, `metadata.*`, `result.*`, …). */
  filter?: Record<string, unknown>;
  /** Поиск по номеру телефона (мин. 3 цифры; 8 / 7 / +7 в начале эквивалентны). */
  phone?: string | null;
  sort?: FeedSort[];
  skip?: number;
  limit?: number;
}

/** Последняя ошибка обработки коммуникации. */
export interface CommunicationLastError {
  stage: string;
  error: string;
  failedAt: string;
}

/** Строка ленты коммуникаций (контракт бэка). */
export interface FeedRow {
  communicationId: string;
  occurredAt: string;
  receivedAt?: string | null;
  provider?: string | null;
  source?: string | null;
  processingStatus: ProcessingStatus;
  /** Менеджер (userSid участника воркспейса). */
  userSid?: string | null;
  direction?: 'inbound' | 'outbound' | null;
  channel?: string | null;
  durationSeconds?: number | null;
  hasMedia?: boolean;
  hasText?: boolean;
  hasTranscript?: boolean;
  lastError?: CommunicationLastError | null;
  resultStatus?: AnalysisResultStatus | null;
  /** Поля schemaDef выбранного плана (шкала оценок /10). */
  result?: Record<string, unknown> | null;
  planVersion?: number | null;
  /** Business-мета без runtime-полей. */
  metadata?: Record<string, unknown>;
}

/** Ответ ленты коммуникаций. */
export interface FeedResponse {
  rows: FeedRow[];
  total: number;
  planSid?: string | null;
}

/** Нетерминальная ли строка ленты (нужно продолжать поллинг). */
export const isFeedRowPending = (row: FeedRow): boolean =>
  row.processingStatus === 'pending' ||
  row.processingStatus === 'ingested' ||
  row.processingStatus === 'preprocessing' ||
  row.resultStatus === 'pending' ||
  row.resultStatus === 'running';

// ─── Контракт дашборда ──────────────────────────────────────────────────────

/** KPI дашборда (шкала оценок /10). */
export interface DashboardKpi {
  callsTotal: number;
  /** Дельта к прошлому периоду; null/отсутствие не заменяется нулём. */
  callsTotalDelta?: number | null;
  avgScore: number | null;
  avgScoreDelta: number | null;
  withResult: number | null;
  withResultDelta: number | null;
  conversion: number | null;
  conversionDelta: number | null;
  withAgreement: number | null;
  withAgreementDelta: number | null;
}

/** Срез по менеджеру в дашборде. */
export interface DashboardManager {
  userSid: string;
  calls: number;
  targeted: number;
  successful: number;
  /** CR в целевое действие (goalAchieved) от целевых, %. */
  crGoalAction: number;
  avgScore: number;
  objectionsTotal: number;
  objectionsIdentified: number;
  objectionsHandled: number | null;
  scriptCompliance: number;
  /** Звонков с неотработанным возражением. */
  unhandledObjections: number;
  /** Звонков с конфликтом (conflict=true). */
  conflicts: number;
  /** Упущенных сделок (temperature=hot && !goalAchieved). */
  missedDeals: number;
}

/** Команда РОПа в текущем директорском dashboard. */
export interface DashboardTeam extends DashboardManager {
  teamSid: string;
  leadUserSid: string;
  branchSid: string | null;
}

/** Филиал среди команд, доступных текущему директору. */
export interface DashboardBranchOption {
  sid: string;
  name: string;
}

/** Ответ `POST /workspaces/{ws}/analytics/dashboard`. */
export interface DashboardResponse {
  kpi: DashboardKpi;
  managers: DashboardManager[];
  teams: DashboardTeam[];
  teamsTotal: number;
  branches: DashboardBranchOption[];
  hasUnassignedTeams: boolean;
  callsDynamics: Array<{date: string; count: number}>;
  /** 5 бакетов в шкале /10. */
  scoreDistribution: Array<{fromScore: number; toScore: number; count: number}>;
  objections: Array<{
    name: string;
    count: number;
    share: number;
    handled: number;
  }>;
  refusals: Array<{name: string; count: number; share: number}>;
  /** Причины ред-флагов (ТЗ 3.5); отсутствующее/null поле трактуется как пустой список. */
  redFlagReasons?: Array<{name: string; count: number; share: number}> | null;
}

/** Тело запроса аналитики (дашборд/очереди). */
/**
 * Срез аналитики по разделу (как `communicationKind` ленты): звонки или встречи,
 * у встреч — опционально тип по `metadata.channel`. Пусто — все коммуникации.
 */
export interface AnalyticsSectionFilter {
  communicationKind?: 'call' | 'meeting';
  /** Только с `communicationKind: 'meeting'`. */
  meetingChannel?: 'offline-meeting' | 'online-meeting';
}

export interface AnalyticsRequest extends AnalyticsSectionFilter {
  dateFrom: string;
  dateTo: string;
  userSids?: string[];
  limit?: number;
  branchSid?: string;
  withoutBranch?: boolean;
  teamSids?: string[];
  teamLimit?: number;
  teamOffset?: number;
}

/** Минимальные рейтинговые агрегаты, не разрешение на чужой профиль. */
export interface WorkspaceRatingRow {
  userSid: string;
  displayName: string;
  callsTotal: number;
  value: number | null;
  rank: number | null;
  previousRank: number | null;
  rankChange: number | null;
}

/** Страница рейтинга с собственной строкой независимо от страницы. */
export interface WorkspaceRatingPage {
  rows: WorkspaceRatingRow[];
  total: number;
  self: WorkspaceRatingRow | null;
}

/** Только поддерживаемые rating-параметры; userSids здесь запрещён. */
export interface WorkspaceRatingRequest extends AnalyticsSectionFilter {
  dateFrom: string;
  dateTo: string;
  metric: 'avg-score' | 'goal-conversion';
  skip: number;
  limit: number;
  branchSid?: string;
  withoutBranch?: boolean;
}

// ─── Контракт очередей дашборда ────────────────────────────────────────────

/** Элемент очереди «требуют внимания» / «лучшие практики». */
export interface QueueItemApi {
  communicationId: string;
  queueType: 'urgent' | 'practices' | string;
  priority: 1 | 2 | 3;
  occurredAt: string;
  userSid?: string | null;
  teamSid?: string | null;
  leadUserSid?: string | null;
  leadName?: string | null;
  branchSid?: string | null;
  branchName?: string | null;
  direction?: 'inbound' | 'outbound' | null;
  /** Канал (`metadata.channel`): `offline-meeting` — очная встреча, `online-meeting` — онлайн. */
  channel?: string | null;
  /** Раздел: meeting — встреча (канал meeting или правило плана встреч), иначе phone. */
  channelType?: 'phone' | 'meeting' | null;
  /** Шкала /10. */
  score?: number | null;
  problemTitle: string;
  recommendation?: string | null;
  clientName?: string | null;
  tags?: string[];
  /** Ред-флаг на звонке (ТЗ 3.4): тег рисуется визуально отличным от негатива. */
  isRedFlag?: boolean;
}

/** Управленческий кейс (агрегат по менеджеру, без привязки к звонку). */
export interface ManagementCase {
  userSid?: string | null;
  priority: 1 | 2 | 3;
  typeTitle: string;
  description: string;
  recommendation?: string | null;
  calls?: number | null;
}

/** Ответ `POST /workspaces/{ws}/analytics/queues`. */
export interface QueuesResponse {
  urgent: QueueItemApi[];
  practices: QueueItemApi[];
  management: ManagementCase[];
}

// ─── Карточка коммуникации ─────────────────────────────────────────────────

/** Ответ `GET /workspaces/{ws}/communications/{sid}/result`. */
export interface CommunicationResultResponse {
  results?: unknown[];
  formResult?: Record<string, unknown> | null;
  transcriptText?: string | null;
  /** Длительность аудио по последней успешной транскрипции, сек (fallback
   *  длительности, когда адаптер приёма её не дал). */
  transcriptDurationSec?: number | null;
  [key: string]: unknown;
}

/** Прогон стадии препроцесса (транскрибация/диаризация) в `/status.preprocessSummary.runs`. */
export interface PreprocessRunRecord {
  sid: string;
  stage?: string | null;
  status: AnalysisResultStatus | string;
  error?: Record<string, unknown> | null;
  resultText?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
}

/** Сводка препроцесса коммуникации (реальный контракт /status). */
export interface PreprocessSummary {
  totalRuns: number;
  lastStatus?: AnalysisResultStatus | string | null;
  runs: PreprocessRunRecord[];
  [key: string]: unknown;
}

/** Счётчики прогонов анализа по планам. */
export interface AnalysisPlanCounts {
  matched: number;
  succeeded: number;
  failed: number;
  running: number;
}

/**
 * Сводка анализа коммуникации (реальный контракт /status): `byPlan` — последний
 * прогон на каждый сметченный план (тот же набор, что `runtime.analysisPlanOutcomes`),
 * `planCounts` — агрегированные счётчики.
 */
export interface AnalysisSummary {
  totalRuns: number;
  lastStatus?: AnalysisResultStatus | string | null;
  byPlan: AnalysisPlanOutcome[];
  planCounts?: AnalysisPlanCounts | null;
  [key: string]: unknown;
}

/**
 * Ошибка в `/status`: ядро кладёт `summary`+`hint` (доменная) либо `error`
 * (runtime-сбой). Рендерим `error ?? summary`.
 */
export interface CommunicationStatusError extends CommunicationLastError {
  summary?: string | null;
  hint?: string | null;
}

/** Ответ `GET /workspaces/{ws}/communications/{sid}/status` (операционный срез). */
export interface CommunicationStatusResponse {
  communicationId?: string;
  processingStatus?: ProcessingStatus;
  lastStage?: string | null;
  workflowId?: string | null;
  runId?: string | null;
  lastError?: Partial<CommunicationStatusError> | null;
  preprocessSummary?: PreprocessSummary | null;
  analysisSummary?: AnalysisSummary | null;
  [key: string]: unknown;
}

// ─── Сырая коммуникация + диагностика приёма/обработки ──────────────────────
// Источник: POST /workspaces/{ws}/communications (bcf GetDTO). В отличие от
// ленты `/feed` (plan-centric, видимость по грантам), сюда попадают ВСЕ
// коммуникации воркспейса, включая те, что не сметчились ни с одним планом или
// упали в обработке. Идентификатор приходит под алиасом `_id`.

/** Итог прогона одного плана анализа на коммуникации (денормализован ядром в `metadata.runtime`). */
export interface AnalysisPlanOutcome {
  planId: string;
  planVersionId?: string | null;
  planKey: string;
  planVersion?: number | null;
  analysisRunId?: string | null;
  status: AnalysisResultStatus | string;
}

/** runtime-срез metadata коммуникации: диагностика приёма и обработки. */
export interface CommunicationRuntimeMeta {
  /** Последняя ошибка обработки (если падало; очищается при старте нового прогона). */
  lastError?: CommunicationLastError | null;
  /** Сколько планов сметчилось при последнем анализе. `0` — ни один план не подошёл. */
  analysisMatchedCount?: number | null;
  /** Итоги по каждому сметченному плану (planKey + статус прогона). */
  analysisPlanOutcomes?: AnalysisPlanOutcome[] | null;
  /** ID последнего Temporal workflow обработки/анализа. */
  lastTemporalWorkflowId?: string | null;
  lastTemporalRunId?: string | null;
  lastAnalysisWorkflowId?: string | null;
  lastWorkflowStartedAt?: string | null;
  [key: string]: unknown;
}

/** Business + runtime metadata коммуникации (нейтральные ключи ядра + colver). */
export interface CommunicationMetadata {
  channel?: string | null;
  direction?: 'inbound' | 'outbound' | string | null;
  ownerSid?: string | null;
  userSid?: string | null;
  workspaceSid?: string | null;
  runtime?: CommunicationRuntimeMeta | null;
  [key: string]: unknown;
}

/**
 * Сырая коммуникация ядра meerec — как отдаёт `POST /workspaces/{ws}/communications`.
 * На проводе идентификатор приходит под алиасом `_id` (см. docs/BACKEND_CONTRACTS.md).
 */
export interface CommunicationRecord {
  _id: string;
  dedupKey?: string | null;
  source?: string | null;
  provider?: string | null;
  externalId?: string | null;
  occurredAt?: string | null;
  receivedAt?: string | null;
  text?: string | null;
  mediaRef?: string | null;
  payloadRef?: string | null;
  processingStatus: ProcessingStatus;
  /** Статус документа: `active` | `deleted` (soft-delete). */
  status?: string | null;
  metadata?: CommunicationMetadata | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** Тело запроса `POST /workspaces/{ws}/communications` (bcf GetDTO). */
export interface CommunicationsListRequest {
  /** MQL-фильтр по полям модели коммуникации (`processingStatus`, `provider`, …). */
  filter?: Record<string, unknown>;
  /** Сортировка: поле модели → `1` (asc) | `-1` (desc). */
  sort?: Record<string, 1 | -1>;
  skip?: number;
  limit?: number;
}

/** Человекочитаемые подписи статусов прогона плана анализа (см. docs/glossary.md). */
export const ANALYSIS_STATUS_LABELS: Record<AnalysisResultStatus, string> = {
  pending: 'В очереди',
  running: 'Выполняется',
  succeeded: 'Готово',
  failed: 'Ошибка',
};

export type {Communication, QueueRecommendation, Employee};

// ─── Контракт «Минуты» (период + сотрудники, только админ) ──────────────────

/**
 * Тело запроса `POST /workspaces/{ws}/analytics/minutes`.
 * Период — `month` либо пара `dateFrom`/`dateTo` (дни включительно, до 366 дней).
 */
export interface MinutesRequest {
  /** Календарный месяц в формате YYYY-MM. */
  month?: string;
  /** Первый день периода YYYY-MM-DD (включительно). */
  dateFrom?: string;
  /** Последний день периода YYYY-MM-DD (включительно). */
  dateTo?: string;
  /** IANA-таймзона границ суток, по умолчанию UTC. */
  timezone?: string;
  /** Только коммуникации этих сотрудников (`metadata.userSid`); не задано — все. */
  userSids?: string[];
}

/** Ответ `analytics/minutes`: длительность коммуникаций за выбранный период. */
export interface MinutesResponse {
  /** YYYY-MM, если период задан месяцем. */
  month?: string | null;
  /** Первый день периода YYYY-MM-DD (включительно). */
  dateFrom: string;
  /** Последний день периода YYYY-MM-DD (включительно). */
  dateTo: string;
  /** Таймзона, в которой считались границы суток. */
  timezone: string;
  /** Сотрудники, по которым считали (null — весь воркспейс). */
  userSids?: string[] | null;
  calls: number;
  /** Коммуникации с известной длительностью, включая нулевую. */
  withDuration: number;
  durationSeconds: number;
  /** Сумма округлённых вверх минут каждого звонка (61 сек → 2 минуты). */
  minutes: number;
}

// ─── Контракт «Нейроаналитик» в карточке менеджера (spec 20) ───────────────

/** Полуоткрытый интервал `[from, to)` в UTC. */
export interface ManagerBriefPeriod {
  from: string;
  to: string;
}

/** Состояние метрики относительно нормы: проблема / норма / сильная сторона. */
export type ManagerBriefMetricStatus = 'down' | 'normal' | 'up';

/** Метрика менеджера на общей шкале 0..5 (scoring v1). */
export interface ManagerBriefMetric {
  key: string;
  /** Готовая подпись — выводится как есть. */
  label: string;
  value: number;
  /** Готовое значение («33.3%», «6») — не переформатировать. */
  displayValue: string;
  unit: 'count' | 'percent' | 'score' | 'pp';
  status: ManagerBriefMetricStatus;
  /** Динамика к предыдущему такому же окну или статус словами. */
  trendHint: string;
  /** Балл 0..1 на общей шкале; направление метрики уже учтено. */
  ratio: number;
  /** Среднее по видимой команде в формате `displayValue`; null — не с кем сравнить. */
  teamAverage: string | null;
}

/** Показатели менеджера без LLM за окно 7 дней (мало звонков — 30). */
export interface ManagerBriefMetrics {
  asOfDate: string;
  periodDays: number;
  period: ManagerBriefPeriod;
  targetedCalls: number;
  overall: {
    score: number;
    maxScore: number;
    grade: string;
    summary: string;
    subScores: {key: string; label: string; value: number}[];
  };
  /** Худшие первыми. */
  items: ManagerBriefMetric[];
}

/** Звонок, упомянутый в тексте анализа: `label` — подпись в тексте («19.10 06:44»). */
export interface ManagerRecommendationCallRef {
  label: string;
  communicationSid: string;
}

/** Часть анализа: «Что получается» или «Что исправить». */
export interface ManagerRecommendationPart {
  /** Тема в 1–4 словах («Возражение «Дорого»»). */
  title: string;
  /** Цифры с нормой команды, поведение в звонках, приём. */
  text: string;
  /** Звонки из текста в порядке упоминания — подписи становятся ссылками; у анализов до v5 нет. */
  calls?: ManagerRecommendationCallRef[];
}

/** Анализ LLM по менеджеру за скользящее окно `periodDays` суток до момента расчёта (`asOfDate`). */
export interface ManagerRecommendation {
  /** Тема главной части: проблема, иначе сильная сторона (у анализов до v4 — единственная). */
  title: string;
  /** Текст главной части. */
  text: string;
  /** «Что исправить»; null/нет — проблем нет или анализ до v4. */
  problem?: ManagerRecommendationPart | null;
  /** «Что получается»; null/нет — сильной стороны нет или анализ до v4. */
  strength?: ManagerRecommendationPart | null;
  /** По скольким целевым звонкам за период построена. */
  targetedCalls: number;
  /** Длина окна в днях: 7, а если за неделю мало звонков — 30. */
  periodDays: number;
  /** `manager-self` — текст на «вы» для самого менеджера. */
  audience: 'supervisor' | 'manager-self';
  asOfDate: string;
  period: ManagerBriefPeriod;
  generatedAt: string;
}

/**
 * Ответ `GET /workspaces/{ws}/analytics/manager-brief/{userSid}` (без вызова LLM)
 * и `POST …/recommendation`. Анализ свой у каждого смотрящего и показывается,
 * пока его не обновят; обновлять можно раз в сутки.
 */
export interface ManagerBrief {
  userSid: string;
  /** Показатели за окно; null — мало целевых звонков и за 30 дней. */
  metrics: ManagerBriefMetrics | null;
  /** Последний анализ смотрящего; null — ещё не получал (кнопка «Получить анализ»). */
  recommendation: ManagerRecommendation | null;
  /** Можно ли сейчас получить/обновить анализ. */
  canRefresh: boolean;
  /** Когда разблокируется «Обновить анализ» (UTC); null — доступно сейчас. */
  nextRefreshAt: string | null;
}
